import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { personasByIdsQuery } from '../api/persona.api';
import { PERSONA_QUERY_KEYS } from '../api/query-keys';
import type { Persona } from '../model/types';
import { usePersonas } from './usePersonas';

/**
 * Fetches a single persona by ID from the workspace list cache.
 * Falls back to a direct by-ID fetch if the list cache is not populated.
 */
export const usePersona = (
  personaId: string | undefined,
  workspaceId: string | null | undefined,
) => {
  const { data: personas, isPending, isError, error, refetch } = usePersonas(workspaceId);

  const persona = useMemo(() => {
    if (!personas || !personaId) return null;
    return personas.find((p) => p.id === personaId) ?? null;
  }, [personas, personaId]);

  return {
    data: persona,
    isPending,
    isError,
    error,
    isNotFound: !isPending && !persona && !!personaId,
    refetch,
  };
};

/**
 * Fetches a single persona by ID using the by-ids endpoint.
 * Cache-first: reads from the byIds cache entry populated by a prior list fetch.
 * The cache key and fallback query both use the same `knownIds` array so they
 * always resolve to the same cache entry — no mismatches.
 *
 * Use this when you have a specific persona ID and don't need the full
 * workspace list — e.g. detail pages reached via a known ID.
 */
export const usePersonaById = (
  personaId: string | undefined,
  workspaceId: string | null | undefined,
  knownIds?: string[],
) => {
  const queryClient = useQueryClient();
  const idsToFetch = useMemo(
    () => (knownIds?.length ? knownIds : personaId ? [personaId] : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [personaId, knownIds?.join(',') ?? ''],
  );

  const cachedPersona = useMemo(() => {
    if (!personaId || !workspaceId || !idsToFetch.length) return undefined;
    const cached = queryClient.getQueryData<Persona[]>(
      PERSONA_QUERY_KEYS.byIds(workspaceId, idsToFetch),
    );
    return cached?.find((p) => p.id === personaId);
  }, [queryClient, personaId, workspaceId, idsToFetch]);

  const { data: fetchedPersonas, isLoading } = useQuery({
    ...personasByIdsQuery(workspaceId, idsToFetch),
    enabled: !cachedPersona && !!personaId && !!workspaceId,
  });

  const persona = cachedPersona ?? fetchedPersonas?.find((p) => p.id === personaId);

  return {
    data: persona,
    isLoading: !cachedPersona && isLoading,
  };
};
